function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { uniqBy, keyBy } from 'lodash-es';
import { Page } from 'seagull';
import ImportWizard from "../../components/ImportWizard/ImportWizard";
import { routes } from "../../constants";
import { useIoContext, useStoreActions } from "../../contexts";
import { useContacts } from "../../hooks/reactQuery/queries/useContacts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useServiceTemplateApi } from "../../hooks/useServiceTemplateApi";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { ErrorResponse } from "../../infra/ErrorResponse";
import { serviceModel, serviceTemplateModel } from "../../models";
import { selectors } from "../../store/state-selectors";
import { useAppConfig, useStoreStatic } from "../../store/store-context";
import { parseAndValidateAgreementCsv } from "./parseAndValidateAgreementCsv";
import { jsx as _jsx } from "react/jsx-runtime";
const title = 'Import agreements';
const servicesEntity = 'service templates';
const contactsEntity = 'contacts';
const csvTemplateUrl = 'https://storage.googleapis.com/anchor-statics/Anchor_Bulk_Import-2024-11-11.zip';
const csvTemplateUrlWithExpirationDate = 'https://storage.googleapis.com/anchor-statics/Anchor_Bulk_Import-2025-01-11.zip';
const csvTemplateUrlWithBundles = 'https://storage.googleapis.com/anchor-statics/Anchor_Import_Agreements_2025-12-07.zip';
const csvTemplateUrlWithBundlesAndExpirationDate = 'https://storage.googleapis.com/anchor-statics/Anchor_Import_Agreements_2026-01-22.zip';
const getServiceUniqueKey = s => s.name;
function applyExistingServiceTemplateId(draft, existingServiceTemplates) {
  var _draft$serviceBundles;
  const applyExistingServiceTemplateId = s => {
    const existingServiceTemplate = existingServiceTemplates[getServiceUniqueKey(s)];
    if (existingServiceTemplate) {
      s.serviceTemplateId = existingServiceTemplate.id;
    }
  };
  const applyExistingServiceTemplateIdForService = service => {
    var _service$bundle;
    applyExistingServiceTemplateId(service);
    (_service$bundle = service.bundle) === null || _service$bundle === void 0 || _service$bundle.services.forEach(applyExistingServiceTemplateId);
  };
  draft.services.forEach(applyExistingServiceTemplateIdForService);
  (_draft$serviceBundles = draft.serviceBundles) === null || _draft$serviceBundles === void 0 || _draft$serviceBundles.bundles.forEach(_ref => {
    let {
      services
    } = _ref;
    return services.forEach(applyExistingServiceTemplateIdForService);
  });
}

/** Imports agreements from a CSV file and saves them as drafts. */
export default function ImportAgreements() {
  const history = useHistory();
  const {
    logBI,
    api
  } = useStoreActions();
  const {
    getNow
  } = useIoContext();
  const serviceTemplateApi = useServiceTemplateApi();
  const [currentSaveData, setCurrentSaveData] = useState();
  const {
    PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD,
    UPLOAD_BUNDLES_BULK
  } = useFeatureFlags();
  const {
    generateUniqueId
  } = useAppConfig();
  const {
    contacts,
    refresh: refreshContacts
  } = useContacts();
  const keyedServiceTemplatesRef = useRef({});
  const {
    getState
  } = useStoreStatic();
  useSetDocumentTitle(title);
  const createServices = async (servicesToSave, setServicesSaveData, withIncludedServiceRealTemplateId) => {
    for (const [index, s] of servicesToSave.entries()) {
      var _template$bundle;
      const template = serviceTemplateModel.fromService(s);
      await serviceTemplateApi.create(serviceTemplateModel.makeServiceTemplate(_objectSpread({
        id: template.id,
        name: template.name,
        description: template.description,
        cost: template.cost,
        billing: template.billing
      }, template.bundle && withIncludedServiceRealTemplateId ? {
        bundle: _objectSpread(_objectSpread({}, template.bundle), {}, {
          services: (_template$bundle = template.bundle) === null || _template$bundle === void 0 ? void 0 : _template$bundle.services.map(withIncludedServiceRealTemplateId)
        })
      } : {})));
      setServicesSaveData(index + 1);
    }
  };
  const getAllNewServicesToCreate = items => {
    const newServices = [];
    const newBundles = [];
    const newIncludedServicesAsServices = [];
    const addServiceIfNotExists = s => {
      const service = serviceModel.fromDTO(s);
      if (service != null && !keyedServiceTemplatesRef.current[service.name]) {
        if (service.bundle) {
          if (!newBundles.find(b => b.name === service.name)) {
            newBundles.push(service);
          }
        } else {
          if (!newServices.find(s => s.name === service.name)) {
            newServices.push(service);
          }
        }
      }
    };
    items.forEach(_ref2 => {
      var _draft$serviceBundles2;
      let {
        draft
      } = _ref2;
      draft.services.forEach(addServiceIfNotExists);
      (_draft$serviceBundles2 = draft.serviceBundles) === null || _draft$serviceBundles2 === void 0 || _draft$serviceBundles2.bundles.forEach(_ref3 => {
        let {
          services
        } = _ref3;
        return services.forEach(addServiceIfNotExists);
      });
    });
    items.forEach(_ref4 => {
      let {
        includedServicesToCreateIfNotExists
      } = _ref4;
      return includedServicesToCreateIfNotExists.forEach(includedService => {
        const service = serviceModel.fromDTO(includedService);
        if (service && !keyedServiceTemplatesRef.current[service.name] && !newIncludedServicesAsServices.find(s => s.name === service.name) && !newServices.find(s => s.name === service.name)) {
          newIncludedServicesAsServices.push(service);
        }
      });
    });
    return {
      newServices,
      newBundles,
      newIncludedServicesAsServices
    };
  };
  const updateKeyedServiceTemplates = async () => {
    const serviceTemplates = await serviceTemplateApi.getActive();
    keyedServiceTemplatesRef.current = keyBy(serviceTemplates, getServiceUniqueKey);
  };
  const analyzeAndCreateAllRequiredServices = async items => {
    await updateKeyedServiceTemplates();
    let savedServices = 0;
    const {
      newServices,
      newBundles,
      newIncludedServicesAsServices
    } = getAllNewServicesToCreate(items);
    const setServicesSaveData = index => setCurrentSaveData({
      entity: servicesEntity,
      saved: savedServices + index,
      total: newServices.length + newIncludedServicesAsServices.length + newBundles.length
    });
    await createServices(newServices, setServicesSaveData);
    savedServices += newServices.length;
    await createServices(newIncludedServicesAsServices, setServicesSaveData);
    savedServices += newIncludedServicesAsServices.length;
    await updateKeyedServiceTemplates();
    await createServices(newBundles, setServicesSaveData, function withIncludedServiceRealTemplateId(includedService) {
      var _keyedServiceTemplate;
      const existingServiceTemplateId = (_keyedServiceTemplate = keyedServiceTemplatesRef.current[includedService.name]) === null || _keyedServiceTemplate === void 0 ? void 0 : _keyedServiceTemplate.id;
      return _objectSpread(_objectSpread({}, includedService), {}, {
        id: existingServiceTemplateId !== null && existingServiceTemplateId !== void 0 ? existingServiceTemplateId : generateUniqueId('included-service')
      });
    });
    await updateKeyedServiceTemplates();
  };
  const analyzeAndCreateAllRequiredContacts = async items => {
    await refreshContacts();
    const newContacts = items.map(i => i.contact);
    const uniqueNewContacts = uniqBy(newContacts, c => c.uniqueKey());
    const existingContactsUniqueKeys = new Set(contacts.map(c => c.uniqueKey()));
    const contactsToSave = uniqueNewContacts.filter(c => !existingContactsUniqueKeys.has(c.uniqueKey()));
    let savedContacts = 0;
    const setContactsSaveData = errorMsg => setCurrentSaveData({
      entity: contactsEntity,
      saved: savedContacts,
      total: contactsToSave.length,
      errorMsg
    });
    setContactsSaveData();
    for (const c of contactsToSave) {
      await api.contact.create(c).catch(err => {
        const msg = new ErrorResponse(err).saveContactErrorText();
        setContactsSaveData(msg);
        throw err;
      });
      savedContacts++;
      setContactsSaveData();
    }

    // Refresh contacts to attach them later to the saved drafts.
    if (contactsToSave.length) {
      await refreshContacts();
    }
    setCurrentSaveData(undefined);
  };
  const onStartSave = async items => {
    await analyzeAndCreateAllRequiredServices(items);
    await analyzeAndCreateAllRequiredContacts(items);
  };
  const getCsvTemplateUrl = () => {
    if (PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD && UPLOAD_BUNDLES_BULK) {
      return csvTemplateUrlWithBundlesAndExpirationDate;
    } else if (PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD) {
      return csvTemplateUrlWithExpirationDate;
    } else if (UPLOAD_BUNDLES_BULK) {
      return csvTemplateUrlWithBundles;
    }
    return csvTemplateUrl;
  };
  return /*#__PURE__*/_jsx(Page, {
    skin: "secondary",
    header: title,
    children: /*#__PURE__*/_jsx(ImportWizard, {
      entityName: "agreements",
      saveEntityName: "drafts",
      currentSaveData: currentSaveData,
      importBtnText: "Import as drafts",
      csvTemplateUrl: getCsvTemplateUrl(),
      testId: "import-agreements",
      onStartSave: onStartSave,
      saveItem: _ref5 => {
        let {
          draft,
          contact: newContact
        } = _ref5;
        const contactUniqueKeyFromDraft = newContact.uniqueKey();
        const contact = contacts.find(c => c.uniqueKey() === contactUniqueKeyFromDraft);
        draft.clientContactId = contact ? contact.id() : null;
        applyExistingServiceTemplateId(draft, keyedServiceTemplatesRef.current);
        return api.draft.save(draft);
      },
      parseAndValidateCsv: async data => {
        var _selectors$user$busin;
        logBI(10901);
        const libraryLegalTermsId = (_selectors$user$busin = selectors.user.businessSettings(getState())) === null || _selectors$user$busin === void 0 ? void 0 : _selectors$user$busin.legalTermsDocumentId;
        const availableLegalTerms = await api.business.getLegalTerms();
        const {
          items,
          errors
        } = await parseAndValidateAgreementCsv({
          data,
          availableLegalTerms,
          now: getNow(),
          PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD,
          generateUniqueId,
          UPLOAD_BUNDLES_BULK,
          defaultDraftsValues: {
            libraryLegalTermsId
          }
        });
        logBI(10902, {
          numAgreements: items.length,
          numErrors: errors.length
        });
        return {
          items,
          errors
        };
      },
      onEvent: name => {
        if (name === 'csvTemplateDownload') {
          logBI(10900);
        } else if (name === 'startImportItems') {
          logBI(10903);
        } else if (name === 'redirectToItems') {
          history.push(routes.draftsDashboard);
        } else if (name === 'afterSaveFailure') {
          logBI(10908);
        } else if (name === 'afterSaveSuccess') {
          logBI(10909);
        }
      }
    })
  });
}